import React, { useState } from 'react';
import { firestore } from '../firebase/firebase';
import logoImage from '../assets/img/luth-logo-32x32.png';

const UpdateApplicantForm = () => {
  const [applicant, setApplicant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);

  // Fetch applicant data based on application number and DOB
  const fetchApplicantData = async () => {
    if (!phone || !dob) {
      setError('Please provide both Phone Number and Date of Birth.');
      return;
    }

    setLoading(true);
    setError(''); // Clear any previous errors

    try {
      const snapshot = await firestore
        .collection('Registrations')
        .where('courseForm.phone', '==', phone)
        .where('courseForm.dob', '==', dob)
        .get();

      if (!snapshot.empty) {
        const applicantData = snapshot.docs[0].data();
        setApplicant(applicantData);
      } else {
        setError('No applicant found with the provided details.');
        setApplicant(null);
      }
    } catch (error) {
      setError('An error occurred while fetching the data.');
      console.error('Error fetching applicant:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!applicant) {
      setError('No applicant data available to update.');
      return;
    }

    try {
      let docId = applicant.courseForm?.uid;

      if (!docId) {
        throw new Error('Document ID is missing');
      }

      docId = docId.replace(/\//g, ''); // Removes the slashes from '2024/0634', making it '20240634'

      await firestore
        .collection('Registrations')
        .doc(docId)
        .update({
          'courseForm.subjects': applicant.courseForm.subjects,
          'courseForm.JambRegistrationScore':
            applicant.courseForm?.examinationScore || '',
        });

      // Show success modal
      setShowModal(true);
    } catch (error) {
      setError('Error updating applicant details.');
      console.error('Error updating applicant:', error);
    }
  };

  const handleSubjectChange = (index, field, value) => {
    const updatedSubjects = [...(applicant.courseForm?.subjects || [])];
    if (index >= updatedSubjects.length) {
      updatedSubjects.push({});
    }
    updatedSubjects[index] = {
      ...updatedSubjects[index],
      [field]: value,
    };

    setApplicant({
      ...applicant,
      courseForm: {
        ...applicant.courseForm,
        subjects: updatedSubjects,
      },
    });
  };

  const closeModal = () => {
    setShowModal(false);
    window.location.reload(); // Refresh the page after closing the modal
  };

  // Render subjects and grades for a single row
  const renderSubjectAndGradeRow = () => {
    const subjects = applicant?.courseForm?.subjects || [{}]; // Default to one empty subject if none exists

    return (
      <tr>
        <td>
          <input
            type="text"
            value={subjects[0]?.sub1 || ''}
            onChange={(e) => handleSubjectChange(0, 'sub1', e.target.value)}
            style={styles.input}
            placeholder="Subject 1"
          />
          <input
            type="text"
            value={subjects[0]?.sub2 || ''}
            onChange={(e) => handleSubjectChange(0, 'sub2', e.target.value)}
            style={styles.input}
            placeholder="Subject 2"
          />
          <input
            type="text"
            value={subjects[0]?.sub3 || ''}
            onChange={(e) => handleSubjectChange(0, 'sub3', e.target.value)}
            style={styles.input}
            placeholder="Subject 3"
          />
          <input
            type="text"
            value={subjects[0]?.sub4 || ''}
            onChange={(e) => handleSubjectChange(0, 'sub4', e.target.value)}
            style={styles.input}
            placeholder="Subject 4"
          />
          <input
            type="text"
            value={subjects[0]?.sub5 || ''}
            onChange={(e) => handleSubjectChange(0, 'sub5', e.target.value)}
            style={styles.input}
            placeholder="Subject 5"
          />
        </td>
        <td>
          <input
            type="text"
            value={subjects[0]?.grade1 || ''}
            onChange={(e) => handleSubjectChange(0, 'grade1', e.target.value)}
            style={styles.input}
            placeholder="Grade 1"
          />
          <input
            type="text"
            value={subjects[0]?.grade2 || ''}
            onChange={(e) => handleSubjectChange(0, 'grade2', e.target.value)}
            style={styles.input}
            placeholder="Grade 2"
          />
          <input
            type="text"
            value={subjects[0]?.grade3 || ''}
            onChange={(e) => handleSubjectChange(0, 'grade3', e.target.value)}
            style={styles.input}
            placeholder="Grade 3"
          />
          <input
            type="text"
            value={subjects[0]?.grade4 || ''}
            onChange={(e) => handleSubjectChange(0, 'grade4', e.target.value)}
            style={styles.input}
            placeholder="Grade 4"
          />
          <input
            type="text"
            value={subjects[0]?.grade5 || ''}
            onChange={(e) => handleSubjectChange(0, 'grade5', e.target.value)}
            style={styles.input}
            placeholder="Grade 5"
          />
        </td>
      </tr>
    );
  };

  return (
    <div style={styles.container}>
      <img src={logoImage} alt="Logo" className="logo" />
      <h2>LUTH SCHOOL APPLICATIONS</h2>
      <h2>Update Applicant</h2>

      {/* Form to enter phone and DOB */}
      <div style={styles.inputContainer}>
        <label style={styles.label}>Phone Number:</label>
        <input
          type="text"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          style={styles.input}
        />
        <label style={styles.label}>Date of Birth:</label>
        <input
          type="date"
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          style={styles.input}
        />
        <button
          onClick={fetchApplicantData}
          disabled={loading}
          style={styles.button}
        >
          {loading ? 'Loading...' : 'Fetch Applicant Data'}
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Display and update form for subjects, grades, and Jamb Registration Score */}
      {applicant && (
        <div>
          <h3>Applicant Details</h3>
          <p>
            <strong>Name:</strong> {applicant.name}
          </p>

          <h3>Subjects and Grades</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th>Subjects</th>
                <th>Grades</th>
              </tr>
            </thead>
            <tbody>{renderSubjectAndGradeRow()}</tbody>
          </table>

          <div style={styles.scoreContainer}>
            <label style={styles.label}>Jamb Registration Score:</label>
            <input
              type="text"
              placeholder="Enter new score"
              value={applicant.courseForm?.examinationScore || ''}
              onChange={(e) =>
                setApplicant({
                  ...applicant,
                  courseForm: {
                    ...applicant.courseForm,
                    examinationScore: e.target.value,
                  },
                })
              }
              style={styles.input}
            />
            <p>
              Current Jamb Score: {applicant.courseForm?.examinationScore || 'N/A'}
            </p>
          </div>

          <button onClick={handleUpdate} style={styles.button}>
            Update Applicant
          </button>
        </div>
      )}

      {/* Modal for success message */}
      {showModal && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <h2>Success</h2>
            <p>Subjects, Grades, and Jamb Registration Score updated successfully!</p>
            <button onClick={closeModal} style={modalStyles.button}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '800px',
    margin: 'auto',
    border: '1px solid #ccc',
    borderRadius: '8px',
    backgroundColor: '#f9f9f9',
  },
  inputContainer: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 'bold',
  },
  input: {
    width: '100%',
    padding: '8px',
    marginBottom: '10px',
    borderRadius: '4px',
    border: '1px solid #ddd',
  },
  button: {
    padding: '10px 20px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
  },
  error: {
    color: 'red',
    marginBottom: '20px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '20px',
  },
  subjectInput: {
    width: '80%',
    padding: '8px',
    marginBottom: '5px',
    borderRadius: '4px',
    border: '1px solid #ddd',
  },
  gradeInput: {
    width: '50px',
    padding: '8px',
    marginBottom: '5px',
    borderRadius: '4px',
    border: '1px solid #ddd',
  },
  scoreContainer: {
    marginBottom: '20px',
  },
};

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modal: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    maxWidth: '500px',
    width: '100%',
    textAlign: 'center',
  },
  button: {
    padding: '10px 20px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
  },
};

export default UpdateApplicantForm;
